/**
 * Krok MAP4 — logika katalogu tematickych vrstev (skupiny D1-D7, oblibene,
 * stav v URL) BEZ DOM, aby sla testovat bez renderu. Vykresluje ji
 * `components/MapLayersPanel.tsx`.
 *
 * Data chodi vyhradne z `GET /v1/duediligence/map/layers`, tedy ze
 * `buildClientMapLayers`/`buildThematicCatalog` nad `SOURCE-REGISTRY.yaml`
 * (@reos/duediligence). Zadna URL vrstvy ani skupina tu neni napsana natvrdo -
 * tenhle modul jen ridi zobrazeni toho, co prislo z registru.
 */
// UX2: `@reos/duediligence` smi do prohlizecoveho bundlu jen jako TYP
// (hlida `test/browser-safe-imports.spec.ts`) - runtime import odtud rozbije
// `vite build` na `node:async_hooks`. Seznam id proto nevozime z kontextu, ale
// bereme ho z klicu `LAYER_LABEL_KEY`, ktere typ `Record<ClientMapLayerId, …>`
// vynucuje jako UPLNE - nova vrstva v kontextu neprojde prekladem bez nazvu tady.
import type { ClientMapLayerId } from '@reos/duediligence';
import type { t } from '@reos/i18n';
import type { ClientMapLayerDto } from './parcel-map-api.js';

export type MessageKeyOf = Parameters<typeof t>[0];

/**
 * Nazvy vrstev pro UI. Jediny seznam pro mapovou kartu i panel vrstev -
 * `ParcelMap` ho importuje odsud, aby stejna vrstva nemela v legende jiny
 * nazev nez v prepinaci.
 */
export const LAYER_LABEL_KEY: Record<ClientMapLayerId, MessageKeyOf> = {
  cadastral: 'dd.map.layer.cadastral',
  orthophoto: 'dd.map.layer.orthophoto',
  orthophoto_archive: 'webAdmin.parcelMap.layer.orthophotoArchive',
  flood_zones: 'dd.map.layer.flood_zones',
  dtm_utilities: 'webAdmin.parcelMap.layer.dtmUtilities',
  heritage: 'webAdmin.parcelMap.layer.heritage',
  buildings: 'webAdmin.parcelMap.layer.buildings',
  protected_areas: 'webAdmin.parcelMap.layer.protectedAreas',
};

/** Vsechna id vrstev, ktera mapova karta zna (klice `LAYER_LABEL_KEY`, viz komentar u importu). */
export const KNOWN_LAYER_IDS = Object.keys(LAYER_LABEL_KEY) as readonly ClientMapLayerId[];

/** Poradi skupin v panelu = poradi ze specifikace (D1 nahore). */
export const MAP_LAYER_GROUP_ORDER = ['D1', 'D2', 'D3', 'D4', 'D5', 'D6', 'D7'] as const;
export type MapLayerGroupId = (typeof MAP_LAYER_GROUP_ORDER)[number];

export const GROUP_LABEL_KEY: Record<MapLayerGroupId, MessageKeyOf> = {
  D1: 'webAdmin.parcelMap.layersPanel.group.D1',
  D2: 'webAdmin.parcelMap.layersPanel.group.D2',
  D3: 'webAdmin.parcelMap.layersPanel.group.D3',
  D4: 'webAdmin.parcelMap.layersPanel.group.D4',
  D5: 'webAdmin.parcelMap.layersPanel.group.D5',
  D6: 'webAdmin.parcelMap.layersPanel.group.D6',
  D7: 'webAdmin.parcelMap.layersPanel.group.D7',
};

/**
 * Skupiny, pro ktere specifikace predepisuje VYSVETLUJICI TEXT, kdyz pro ne
 * zadna vrstva neexistuje - misto prazdneho oddilu (bod 4 a 5 briefu).
 * Prazdna skupina bez textu by vypadala jako vada nacitani.
 */
export const GROUP_EMPTY_NOTICE_KEY: Partial<Record<MapLayerGroupId, MessageKeyOf>> = {
  D2: 'webAdmin.parcelMap.layersPanel.empty.D2',
  D7: 'webAdmin.parcelMap.layersPanel.empty.D7',
};

/** Vrstva katalogu = polozka s vyplnenym blokem `map:` z registru. */
export type ThematicLayerDto = ClientMapLayerDto & {
  readonly map: NonNullable<ClientMapLayerDto['map']>;
};

/**
 * Tematicke vrstvy z odpovedi endpointu. Podminka "dostupna + ma `map:`" je
 * zamerne TOTOZNA s `buildThematicCatalog()` v @reos/duediligence, ktery ji
 * uplatnuje na serveru (report, MCP). Druha kopie tu je proto, ze
 * `apps/web-admin` smi z kontextu importovat jen TYPY (hranice kontextu,
 * CLAUDE.md §1.4) - runtime funkci sem zatahnout nelze. Pole `map` je
 * jediny priznak, podle ktereho se filtruje, takze obe strany drzi stejna
 * data z registru; kdyz se podminka zmeni, meni se na obou mistech.
 */
export function thematicLayers(
  layers: readonly ClientMapLayerDto[],
): readonly ThematicLayerDto[] {
  return layers.filter(
    (layer): layer is ThematicLayerDto =>
      layer.status === 'available' && layer.map !== null && layer.map !== undefined,
  );
}

/** Tematicke vrstvy po skupinach, v poradi specifikace; prazdne skupiny zustavaji. */
export function groupThematicLayers(
  layers: readonly ClientMapLayerDto[],
): readonly { readonly group: MapLayerGroupId; readonly layers: readonly ThematicLayerDto[] }[] {
  const thematic = thematicLayers(layers);
  return MAP_LAYER_GROUP_ORDER.map((group) => ({
    group,
    layers: thematic.filter((layer) => layer.map.group === group),
  }));
}

/**
 * Poradi dlazdic „Nejoblibenejsi" podle specifikace (uzemni plan, kde lze
 * stavet, statistiky obce, site, vecna bremena, prodeje, povodne, pamatky).
 * Vrstvy, ktere v registru jeste nejsou, se v panelu NEVYKRESLI - dlazdice
 * nikdy neodkazuje na vrstvu, kterou mapa neumi zobrazit.
 */
export const FAVOURITE_LAYER_ORDER: readonly ClientMapLayerId[] = [
  'dtm_utilities',
  'flood_zones',
  'heritage',
  'protected_areas',
  'cadastral',
];

export const FAVOURITE_TILE_LIMIT = 8;

export function favouriteLayers(
  layers: readonly ClientMapLayerDto[],
): readonly ThematicLayerDto[] {
  const thematic = thematicLayers(layers);
  return FAVOURITE_LAYER_ORDER.map((id) => thematic.find((layer) => layer.id === id)).filter(
    (layer): layer is ThematicLayerDto => layer !== undefined,
  ).slice(0, FAVOURITE_TILE_LIMIT);
}

/** Hledani vrstvy: bez diakritiky a bez ohledu na velikost pismen. */
export function matchesLayerQuery(label: string, query: string): boolean {
  const normalize = (value: string): string =>
    value
      .normalize('NFD')
      .replace(/\p{Diacritic}/gu, '')
      .toLocaleLowerCase('cs');
  const needle = normalize(query.trim());
  if (needle === '') return true;
  return normalize(label).includes(needle);
}

/** Zapnuta vrstva a jeji pruhlednost (0-1). */
export type LayerVisibilityState = ReadonlyMap<ClientMapLayerId, number>;

const URL_PARAM = 'mapLayers';

/**
 * Stav panelu v URL: `?mapLayers=flood_zones:0.5,heritage:0.6`. Odkaz na mapu
 * tak nese zapnute vrstvy i jejich pruhlednost (bod 2 briefu). Neznama vrstva
 * nebo pruhlednost mimo 0-1 se TICHE zahodi - URL je vstup od uzivatele, ne
 * duvod k rozbite obrazovce.
 */
export function parseLayerStateFromSearch(
  search: string,
  knownIds: readonly ClientMapLayerId[] = KNOWN_LAYER_IDS,
): LayerVisibilityState {
  const raw = new URLSearchParams(search).get(URL_PARAM);
  const state = new Map<ClientMapLayerId, number>();
  if (raw === null || raw === '') return state;
  for (const part of raw.split(',')) {
    const [id, opacityRaw] = part.split(':');
    if (id === undefined) continue;
    const known = knownIds.find((candidate) => candidate === id);
    if (known === undefined) continue;
    const opacity = opacityRaw === undefined ? Number.NaN : Number.parseFloat(opacityRaw);
    if (!Number.isFinite(opacity) || opacity < 0 || opacity > 1) continue;
    state.set(known, opacity);
  }
  return state;
}

/** Hodnota parametru `mapLayers`; prazdny string, kdyz neni zapnuta zadna vrstva. */
export function serializeLayerState(state: LayerVisibilityState): string {
  return [...state.entries()]
    .map(([id, opacity]) => `${id}:${String(Math.round(opacity * 100) / 100)}`)
    .join(',');
}

/** Nova URL se stavem panelu (zachova ostatni parametry). */
export function layerStateToSearch(search: string, state: LayerVisibilityState): string {
  const params = new URLSearchParams(search);
  const value = serializeLayerState(state);
  if (value === '') params.delete(URL_PARAM);
  else params.set(URL_PARAM, value);
  const next = params.toString();
  return next === '' ? '' : `?${next}`;
}
